import { ReactNode } from 'react';
import styled from 'styled-components';
import { CircleSpinner } from 'ui/spinner';
import { colors, spacing } from 'ui/theme';
import { useCollapseStateContext } from 'ui/treeView/context/CollapseStateContext';
import { pxToRem } from 'utils/convert';
import { useSnippetsContext } from '../context';
import { Cell } from './Cell';
import { GRID } from './constants';
import { ExpansionTrigger } from './ExpansionTrigger';

export const ROW_TEST_ID = 'snippets-row';

type StyledRowProps = {
  gridLayout: string;
  color: string;
  depth: number;
  isCampaign: boolean;
};

const StyledRow = styled.div<StyledRowProps>`
  align-items: center;
  background: ${({ color }) => color};
  column-gap: ${spacing.base};
  display: grid;
  grid-template-columns: ${({ gridLayout }) => gridLayout};
  height: ${({ depth, isCampaign }) => {
    if (isCampaign) {
      return pxToRem(64);
    }
    return depth > 1 ? spacing.x3 : spacing.x6;
  }};
  padding: ${({ isCampaign }) => {
    return isCampaign ? `${pxToRem(8)} ${pxToRem(40)}` : `${spacing.base} ${pxToRem(40)}`;
  }};

  .indent {
    margin-left: ${({ depth }) => pxToRem(16 * (depth - 1))};
  }
`;

const StyledBreakdownLoadingCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

type RowProps = {
  gridLayout?: string;
  color?: string;
  path?: string;
  depth?: number;
  isActive?: boolean;
  toggleCellIndex?: number;
  toggleLabel?: ReactNode;
  cells: ReactNode[];
  isExpandable?: boolean;
  redirect?: boolean;
  snippetId?: number;
  isAtDepthLimit?: boolean;
  isAtCampaignLimit?: boolean;
  isAtVariationLimit?: boolean;
  isCampaign?: boolean;
  isBreakdownLoadingRow?: boolean;
};

export const Row = ({
  gridLayout = GRID,
  color = colors.white,
  path = '',
  depth = 0,
  toggleCellIndex = 0,
  toggleLabel = '',
  cells = [],
  isExpandable,
  isActive,
  redirect,
  snippetId,
  isAtDepthLimit,
  isAtCampaignLimit,
  isAtVariationLimit,
  isCampaign = false,
  isBreakdownLoadingRow = false,
}: RowProps) => {
  const { updateIsExpanded } = useSnippetsContext();
  const { toggle, isCollapsed } = useCollapseStateContext();

  const toggleCollapse = () => {
    toggle(path);
    updateIsExpanded();
  };

  const showToggleIcon = isExpandable || isAtDepthLimit || isAtVariationLimit;

  return (
    <StyledRow
      data-test={ROW_TEST_ID}
      gridLayout={isBreakdownLoadingRow ? '' : gridLayout}
      color={color}
      onClick={toggleCollapse}
      depth={depth}
      isCampaign={isCampaign}
    >
      {isBreakdownLoadingRow ? (
        <StyledBreakdownLoadingCell>
          <CircleSpinner size={16} />
        </StyledBreakdownLoadingCell>
      ) : (
        cells.map((c, i) => (
          <Cell
            key={i}
            value={c}
            isIndented={i === toggleCellIndex}
            trigger={
              i === toggleCellIndex && showToggleIcon ? (
                <ExpansionTrigger
                  isCollapsed={isCollapsed}
                  isExpandable={isExpandable}
                  isAtCampaignLimit={isAtCampaignLimit}
                  isAtDepthLimit={isAtDepthLimit}
                  path={path}
                  toggleLabel={toggleLabel}
                />
              ) : undefined
            }
            snippetId={snippetId}
            redirect={redirect}
            isActive={isActive}
          />
        ))
      )}
    </StyledRow>
  );
};
